import React, { useEffect, useRef, useState } from 'react'
import currency from 'currency.js'
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons'

import { Button, Link, Badge, Text } from '@/ui'
import { mtc, TextColors, TypographyStyles } from '@/styles'
import { useFetchResponse, useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { getCurrentUser } from '@/selectors/user.selectors'
import { formatCurrency } from '@/utils/currencyHelpers'
import { CATCHUP_BOOKKEEPING_PRICE_PER_MONTH } from '@/constants/pricingConstants'
import { fetchCatchUpBKCoupon } from '@/components/CatchUpBookkeepingStatus/catchUpBookkeepingStatus.slice'
import {
  CatchUpBkTreatment,
  discountedCatchUpBkAmount,
} from '@/features/user/signup/payment/helpers'
import { useCatchupBookkeepingPrice } from '@/features/user/signup/payment/hooks'
import { BILLING_INTERVALS, type BillingInterval } from './recommendation.types'

import { ANALYTICS_SURFACES } from '../analytics/compliance-analytics.constants'
import { useComplianceAnalytics } from '../analytics/use-compliance-analytics'
import { useComplianceVerdict } from '../analytics/use-compliance-verdict'
import { fetchReport } from '../compliance.actions'
import { selectReport } from '../compliance.selectors'
import {
  COMPLIANCE_PATHS,
  FUNNEL_MAX_W_CLS,
  FUNNEL_PAGE_CLS,
} from '../lib/constants'
import { buildConsultUrl } from '../lib/consultLink'

import {
  dueTodayLines,
  dueTodaySavingsCents,
  formatLineAmount,
  money,
  type DueTodayLine,
  type DueTodayInput,
} from './dueToday'

import { BillingIntervalToggle } from './BillingIntervalToggle'
import { CompliancePlanSection } from './CompliancePlanSection'
import { usePlanCheckout } from './usePlanCheckout'
import { useCountdown, formatRemaining } from './useCountdown'
import { ConsultCard } from './ConsultCard'

const CURRENT_YEAR = new Date().getFullYear()
const CUBK_ICON_URL =
  'https://heard-images.s3.amazonaws.com/assets/Auto%20Cat.svg'

/** Presentational: the parent owns the amounts, because the due-today total has
 *  to keep agreeing with them even in the renders where this section isn't on
 *  screen at all. */
const CatchUpBookkeepingSection: React.FC<{
  monthCount: number
  originalAmount: currency
  discountedAmount: currency
  percentOff: number | null | undefined
  waived: boolean
  deferred: boolean
  countdownLabel: string | null
}> = ({
  monthCount,
  originalAmount,
  discountedAmount,
  percentOff,
  waived,
  deferred,
  countdownLabel,
}) => {
  const hasPromo = waived || Boolean(percentOff)
  const isFree = waived || percentOff === 100
  // The countdown badge already says the fee is free, and says why. A second
  // badge beside the price repeats it in less detail.
  const showPriceBadge = hasPromo && !countdownLabel

  return (
    <section className="flex flex-col gap-4 rounded-2xl bg-neutral-25 p-6">
      {/* The price is a sibling rather than nested with the name so that grid
          placement can put it in the third column without also putting it last
          in the stack below `sm`, where the phone reads name, price, prose. */}
      <div className="flex flex-col gap-4 sm:grid sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:gap-x-6 sm:gap-y-4">
        {/* Decorative. Hidden on a phone, where its 80px indented every row
            beneath it and took the width the countdown badge needed. */}
        <img
          src={CUBK_ICON_URL}
          alt=""
          className="hidden h-20 w-20 self-center sm:col-start-1 sm:row-span-2 sm:row-start-1 sm:block"
        />
        {/* One cell so an absent badge closes up rather than leaving its
            row behind. */}
        <div className="flex flex-col gap-4 sm:col-start-2 sm:row-start-1">
          {countdownLabel && (
            <div className="w-fit">
              <Badge color="green" variant="secondary">
                {`Free if you sign up within ${countdownLabel}`}
              </Badge>
            </div>
          )}
          <div className="flex flex-col gap-0.5">
            <Text
              variant={TypographyStyles.DISPLAY_XS}
              color={TextColors.HEADER}
            >
              Catch up Bookkeeping
            </Text>
            <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
              {`${monthCount} month${monthCount === 1 ? '' : 's'} at ${formatCurrency(CATCHUP_BOOKKEEPING_PRICE_PER_MONTH, { hideDecimalsIfZero: true })}/mo for ${CURRENT_YEAR} taxes`}
            </Text>
          </div>
        </div>
        <div className="flex flex-col items-start gap-0.5 sm:col-start-3 sm:row-start-1 sm:items-end">
          <div className="flex items-center gap-2">
            {showPriceBadge && (
              <Badge color="green" variant="secondary">
                {isFree ? 'Free' : `${percentOff}% off`}
              </Badge>
            )}
            <Text
              variant={TypographyStyles.DISPLAY_MD}
              color={TextColors.HEADER}
            >
              {formatCurrency(discountedAmount, {
                hideDecimalsIfZero: true,
              })}
            </Text>
          </div>
          {hasPromo && (
            <Text
              variant={TypographyStyles.BODY_SM}
              color={TextColors.MUTED}
              className="line-through"
            >
              {formatCurrency(originalAmount, { hideDecimalsIfZero: true })}
            </Text>
          )}
          <Text variant={TypographyStyles.BODY_XS} color={TextColors.MUTED}>
            {/* Only a deferred fee names the trial. A waived one has no trial
                to defer to and no charge coming, so it would promise both
                falsely. */}
            {deferred ? 'One time fee, after your trial' : 'One time fee'}
          </Text>
        </div>
        {/* No month count in the sentence: it is derived from a launch date
            the intake only asks for once there are clients, so someone who
            answered "not yet" has none. */}
        <Text
          variant={TypographyStyles.BODY_SM}
          color={TextColors.MUTED}
          className="sm:col-start-2 sm:row-start-2"
        >
          We organize the books behind you so this year starts clean. Organized
          books are the backbone of your financial system, and they inform
          accurate deduction tracking and tax filing.
        </Text>
      </div>
    </section>
  )
}

/** One row of the due-today breakdown: what it is on the left, what it costs on
 *  the right. The amount never wraps, so a long description gives way first. */
const TotalLine: React.FC<{ line: DueTodayLine }> = ({ line }) => (
  <div className="flex flex-col gap-0.5">
    <div className="flex items-baseline justify-between gap-3">
      <Text variant={TypographyStyles.BODY_MD} color={TextColors.EMPHASIS}>
        {line.label}
      </Text>
      {/* Tabular figures so the amounts form a straight column however wide
          each one is. */}
      <Text
        variant={TypographyStyles.LABEL_MD}
        color={
          line.amountCents < 0 ? TextColors.HIGHLIGHT : TextColors.EMPHASIS
        }
        textAlign="right"
        className="shrink-0 tabular-nums"
      >
        {formatLineAmount(line.amountCents)}
      </Text>
    </div>
    {line.detail && (
      <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
        {line.detail}
      </Text>
    )}
  </div>
)

/** Placeholders shaped like the real cards and the summary beside them, so the
 *  page does not reflow when the offers land. */
const Bar: React.FC<{ className: string }> = ({ className }) => (
  <div className={mtc('animate-pulse rounded bg-neutral-100', className)} />
)

const PlanSkeleton: React.FC = () => (
  <div className="flex flex-col gap-6 md:flex-row" aria-hidden>
    {[0, 1].map((key) => (
      <div key={key} className="min-w-[204px] flex-1">
        <div className="flex h-full flex-col gap-4 rounded-xl border border-neutral-200 bg-neutral-0 p-6">
          <div className="flex flex-col gap-2">
            <Bar className="h-3 w-1/2" />
            <div className="flex flex-col gap-4">
              <Bar className="h-6 w-1/3" />
              <div className="flex flex-col gap-1">
                <Bar className="h-8 w-2/5" />
                <Bar className="h-4 w-1/4" />
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <Bar className="h-3 w-full" />
            <Bar className="h-3 w-4/5" />
            <Bar className="h-3 w-1/3" />
          </div>
          <Bar className="mt-auto h-10 w-full rounded-lg" />
        </div>
      </div>
    ))}
  </div>
)

/** The order summary's own placeholder: two line-item groups, a total, and the
 *  button, so the rail holds its width while the offers resolve. */
const SummarySkeleton: React.FC = () => (
  <div className="flex flex-col gap-4" aria-hidden>
    <Bar className="h-3 w-1/2" />
    <div className="flex flex-col gap-4">
      {[0, 1].map((key) => (
        <div key={key} className="flex flex-col gap-0.5">
          <div className="flex items-baseline justify-between gap-3">
            <Bar className="h-4 w-1/2" />
            <Bar className="h-4 w-1/4" />
          </div>
          <Bar className="h-3 w-2/5" />
        </div>
      ))}
    </div>
    <div className="flex flex-col gap-8 border-t border-neutral-200 pt-4">
      <div className="flex items-baseline justify-between gap-3">
        <Bar className="h-5 w-2/5" />
        <Bar className="h-5 w-1/3" />
      </div>
      <div className="flex flex-col gap-2">
        <Bar className="h-10 w-full rounded-lg" />
        <Bar className="h-3 w-3/4 self-center" />
      </div>
    </div>
  </div>
)

export const CompliancePlanPricing: React.FC = () => {
  const dispatch = useAppDispatch()
  const user = useReselector(getCurrentUser)
  // `isPql` is decided server-side at intake submit and only travels on the
  // report, so it is read from the store rather than re-derived here — a second
  // copy of the PQL rule on the web could drift from the one the CRM sync uses.
  const report = useReselector(selectReport)
  const hasReport = Boolean(report)

  useEffect(() => {
    // Mount-only, and only when the store is cold. Arriving from the report —
    // the ordinary path — costs nothing; a direct load or refresh pays one
    // fetch, and the card lands in the rail, so nothing in the plan column
    // shifts when it does. Not re-run on failure: no consult card is the right
    // outcome for an unknown verdict, and retrying would loop.
    if (!hasReport) dispatch(fetchReport())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch])

  const showConsult = report?.isPql === true

  // Annual is the default view — most offers save money paying annually, so
  // that's the plan most people should see first. `plan.interval` is what
  // actually applies: it falls back to monthly when no offer prices annually.
  const [requestedInterval, setRequestedInterval] = useState<BillingInterval>(
    BILLING_INTERVALS.year
  )

  // Fetched ahead of usePlanCheckout, and keyed to the requested interval rather
  // than the effective one, because the waiver it carries is an input to that
  // hook. Keying it to plan.interval would need the hook's own output first.
  const coupon = useFetchResponse(
    fetchCatchUpBKCoupon,
    requestedInterval === BILLING_INTERVALS.year
  )
  // The promo waives the fee rather than discounting it, so there is no coupon to
  // read: a deadline still in the future IS the offer. useCountdown's tick is the
  // only thing that expires it, and it drives the waiver too, so a memoized plan
  // section would keep checking out as waived past the deadline.
  const remainingMs = useCountdown(coupon?.promoExpiresAt)
  const promoLive = remainingMs !== null && remainingMs > 0

  const plan = usePlanCheckout(requestedInterval, promoLive)

  const verdict = useComplianceVerdict()
  const { trackViewedPlansPage, trackSelectedPlan } = useComplianceAnalytics(
    ANALYTICS_SURFACES.SignupFunnel
  )
  const previewOffer =
    'previewOffer' in plan.state ? plan.state.previewOffer : null
  const recommendedTier =
    ('offers' in plan.state ? plan.state.offers : []).find(
      (offer) => offer.isRecommended
    )?.tier ?? null
  const recommendationResolved = plan.state.status !== 'loading'
  const viewReportedRef = useRef(false)

  // Deferred until the offers settle: on a cold load they are still in flight at
  // mount, and firing there would report no recommendation for most visitors.
  useEffect(() => {
    if (!recommendationResolved || viewReportedRef.current) return
    viewReportedRef.current = true
    trackViewedPlansPage({ recommended_tier: recommendedTier, verdict })
  }, [recommendationResolved, recommendedTier, verdict, trackViewedPlansPage])

  const interval = plan.interval
  const cubkChargedToday = plan.catchUpBk === CatchUpBkTreatment.chargeAtSignup
  // Same source checkout bills from — the month count is server-derived off the
  // practice start date, so quoting it from anything else would quote a number
  // Stripe won't charge. Undefined means no catch-up months apply.
  const catchupBookkeepingPrice = useCatchupBookkeepingPrice()
  const cubkMonthCount = catchupBookkeepingPrice?.monthCount ?? 0
  const cubkOriginalAmount = catchupBookkeepingPrice?.amount ?? currency(0)
  const cubkWaived = plan.catchUpBk === CatchUpBkTreatment.waived
  const cubkDiscountedAmount = cubkWaived
    ? currency(0)
    : discountedCatchUpBkAmount(cubkOriginalAmount, coupon?.percent_off)
  // Shown whenever catch-up months apply, waived or not. Someone getting this
  // free should be told so, struck through and marked $0, rather than the fee
  // silently never appearing.
  const cubkApplies = cubkMonthCount > 0
  const cubkFeeCents =
    cubkApplies && cubkChargedToday ? cubkDiscountedAmount.intValue : 0

  const planDueTodayCents = plan.freeTrialEnabled ? 0 : plan.previewPriceCents
  const totalDueCents =
    planDueTodayCents !== null ? planDueTodayCents + cubkFeeCents : null
  const isAnnual = interval === BILLING_INTERVALS.year
  // Stated on every plan, not just annual and trial ones: a monthly plan's
  // due-today total includes any catch-up fee, so the recurring amount is not
  // the number above the button either.
  const renewalNote = `Then ${money(plan.previewPriceCents ?? 0)} billed ${isAnnual ? 'annually' : 'monthly'}${plan.freeTrialEnabled ? ' after your trial ends' : ''}`
  // One line under the button rather than two: the renewal and who takes the
  // payment are both footnotes to it.
  const payFootnote = `${renewalNote} · Secured by Stripe`
  const dueToday: DueTodayInput = {
    planName: plan.previewName,
    planPreviewCents: plan.previewPriceCents,
    isAnnual,
    freeTrialEnabled: plan.freeTrialEnabled,
    cubkApplies,
    cubkChargedToday,
    cubkWaived,
    cubkMonthCount,
    cubkOriginalCents: cubkOriginalAmount.intValue,
    cubkDiscountedCents: cubkDiscountedAmount.intValue,
    percentOff: coupon?.percent_off,
  }
  const breakdown = dueTodayLines(dueToday)
  const savingsCents = dueTodaySavingsCents(dueToday)

  // `ready` is the only state carrying an offer, so this is the whole test for
  // "there is something to quote". Inferring it from a loading flag plus a
  // total let a free trial through: it zeroes the plan charge, so the total
  // resolved to $0 with no offer behind it.
  const quotable = plan.state.status === 'ready' && totalDueCents !== null
  // The rail is held open by its own placeholder while the offers load, so the
  // plan column keeps its width instead of the rail popping in beside it.
  const hasRail = quotable || plan.state.status === 'loading'

  return (
    // Clears the pay bar pinned below `lg`. On the root, not the content, so
    // the consult bar after it clears the bar too.
    <div
      className={mtc('flex w-full flex-col gap-2', quotable && 'max-lg:pb-36')}
    >
      <div className={FUNNEL_PAGE_CLS}>
        <Link
          href={COMPLIANCE_PATHS.report}
          iconLeft={faArrowLeft}
          color={TextColors.MUTED}
          // Two things the Link atom gives no prop for: it bakes in `label-sm`,
          // and it hardcodes its icon to Tailwind's `text-gray-500` rather than
          // the design system's neutral-500.
          className="w-fit body-sm [&_svg]:text-neutral-500"
        >
          Back to report
        </Link>
      </div>
      {/* Content sits narrower than the funnel's chrome, except when the rail is
          present, where it takes the full funnel width so the rail has somewhere
          to go that isn't the plan cards' space. The chrome above holds still
          either way, which is the point of that width. */}
      <div
        className={mtc(
          // Clears the consult bar, which is only pinned from `lg`.
          'pb-16',
          showConsult && 'lg:pb-40',
          hasRail ? FUNNEL_PAGE_CLS : 'mx-auto w-full max-w-3xl px-6'
        )}
      >
        {/* Two columns only when there is something to put in the second one:
            otherwise the 300px track would still be reserved and the plans
            would sit narrower than they do today for no visible reason. */}
        <div
          className={mtc(
            'grid grid-cols-1 gap-x-8 gap-y-4',
            hasRail && `lg:grid-cols-[1fr_300px] ${FUNNEL_MAX_W_CLS}`
          )}
        >
          {plan.state.status === 'ready' && (
            <div className="lg:col-start-1 lg:row-start-1">
              <Text
                variant={TypographyStyles.DISPLAY_MD}
                color={TextColors.HEADER}
              >
                Here’s what we recommend
              </Text>
            </div>
          )}
          {/* The row is held open while the offers load. `hasAnnualOffer` is
              false until they land, so without a placeholder the toggle appears
              late and pushes the cards down. */}
          {plan.state.status === 'loading' ? (
            <div className="mt-4 lg:col-start-1 lg:row-start-2" aria-hidden>
              <Bar className="h-5 w-40" />
            </div>
          ) : (
            plan.hasAnnualOffer && (
              <div className="mt-4 lg:col-start-1 lg:row-start-2">
                <BillingIntervalToggle
                  interval={interval}
                  onChange={setRequestedInterval}
                  savingHighlight={plan.savingHighlight}
                />
              </div>
            )
          )}
          <div
            data-testid="plan-column"
            className="flex min-w-0 flex-col gap-6 lg:col-start-1 lg:row-start-3"
          >
            {plan.state.status === 'loading' && <PlanSkeleton />}
            <CompliancePlanSection plan={plan} />
            {cubkApplies && (
              <CatchUpBookkeepingSection
                monthCount={cubkMonthCount}
                originalAmount={cubkOriginalAmount}
                discountedAmount={cubkDiscountedAmount}
                percentOff={cubkWaived ? undefined : coupon?.percent_off}
                waived={cubkWaived}
                deferred={
                  plan.catchUpBk === CatchUpBkTreatment.deferToPostTrial
                }
                countdownLabel={
                  promoLive && remainingMs !== null
                    ? formatRemaining(remainingMs)
                    : null
                }
              />
            )}
          </div>
          {hasRail && (
            // Placed in the plan cards' own row, so it starts level with their
            // top edge rather than with the heading or the toggle above them.
            // Stacked, `mt-2` tops the grid's 16px row gap up to 24px.
            <aside className="flex flex-col gap-4 max-lg:mt-2 lg:col-start-2 lg:row-start-3 lg:border-l lg:border-neutral-200 lg:pl-6">
              {!quotable ? (
                <SummarySkeleton />
              ) : (
                <>
                  <Text
                    variant={TypographyStyles.EYEBROW}
                    color={TextColors.MUTED}
                  >
                    Order Summary
                  </Text>
                  {/* Built by `dueTodayLines`, which is where the rule that these
                  sum to the total below is enforced and tested. */}
                  <div className="flex flex-col gap-4">
                    {breakdown.map((line) => (
                      <TotalLine key={line.key} line={line} />
                    ))}
                  </div>
                  <div className="flex flex-col gap-8 border-t border-neutral-200 pt-4">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-baseline justify-between gap-3">
                        <Text
                          variant={TypographyStyles.LABEL_LG}
                          color={TextColors.HEADER}
                        >
                          Due today
                        </Text>
                        <Text
                          variant={TypographyStyles.LABEL_LG}
                          color={TextColors.HEADER}
                          textAlign="right"
                          className="shrink-0 font-semibold"
                        >
                          {money(totalDueCents)}
                        </Text>
                      </div>
                      {/* A waived fee sits at zero in the breakdown, so this
                          is the only place its saving is stated. */}
                      {savingsCents > 0 && (
                        <Text
                          variant={TypographyStyles.BODY_SM}
                          color={TextColors.HIGHLIGHT}
                        >
                          {`You save ${money(savingsCents)}`}
                        </Text>
                      )}
                    </div>
                    {/* Pinned below `lg`, where the rail stacks under the
                        plans. Moved rather than duplicated, so there is still
                        one checkout handler. Not the DS `StickyBottomNav`
                        (a Back/Continue wizard bar, no loading state, nowhere
                        for the footnote), same call `ReportStickyCta` makes. */}
                    <div className="flex flex-col gap-2 max-lg:fixed max-lg:inset-x-0 max-lg:bottom-0 max-lg:z-50 max-lg:border-t max-lg:border-neutral-200 max-lg:bg-neutral-0 max-lg:px-6 max-lg:py-4">
                      <Button
                        size="lg"
                        className="w-full"
                        disabled={plan.busy}
                        loading={plan.busy}
                        onClick={() => {
                          if (previewOffer) {
                            trackSelectedPlan({
                              tier: previewOffer.tier,
                              interval,
                              is_recommended:
                                previewOffer.tier === recommendedTier,
                            })
                          }
                          plan.payPreview()
                        }}
                      >
                        {`Pay ${money(totalDueCents)} now`}
                      </Button>
                      <Text
                        variant={TypographyStyles.BODY_XS}
                        color={TextColors.MUTED}
                        textAlign="center"
                      >
                        {payFootnote}
                      </Text>
                    </div>
                  </div>
                </>
              )}
            </aside>
          )}
        </div>
      </div>
      {showConsult && <ConsultCard consultUrl={buildConsultUrl(user)} />}
    </div>
  )
}
